import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const MailContext = createContext();

const API = process.env.REACT_APP_API_URL || 'https://barua.ellertek.co.tz/api';

function getToken() { return localStorage.getItem('ellertek-token'); }

async function apiFetch(path, method = 'GET', body = null) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

function colorForEmail(email) {
  const colors = ['teal', 'blue', 'amber', 'pink', 'purple', 'green'];
  let hash = 0;
  for (const c of (email || '')) hash = (hash << 5) - hash + c.charCodeAt(0);
  return colors[Math.abs(hash) % colors.length];
}

function enrichEmail(email) {
  if (!email.from.color) {
    email.from.color = colorForEmail(email.from.email || '');
  }
  return email;
}

export function MailProvider({ children }) {
  const [emails, setEmails] = useState([]);
  const [loadingFolder, setLoadingFolder] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeEmail, setActiveEmail] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState(null);
  const [undoQueue, setUndoQueue] = useState([]);
  const [search, setSearch] = useState('');
  const undoTimerRef = useRef(null);
  const loadedFolders = useRef(new Set());

  const fetchFolder = useCallback(async (folder, force = false) => {
    if (!force && loadedFolders.current.has(folder)) return;
    setLoadingFolder(true);
    setFetchError('');
    try {
      const endpoint = folder === 'scheduled' ? '/mail/scheduled' : `/mail/${folder}`;
      const data = await apiFetch(endpoint);
      const fetched = (data.emails || []).map(enrichEmail).map(e => ({ ...e, folder }));
      setEmails(prev => {
        const withoutFolder = prev.filter(e => e.folder !== folder);
        return [...withoutFolder, ...fetched];
      });
      loadedFolders.current.add(folder);
    } catch (err) {
      setFetchError(`Could not load ${folder}: ${err.message}`);
    } finally {
      setLoadingFolder(false);
    }
  }, []);

  useEffect(() => { fetchFolder('inbox'); }, [fetchFolder]);

  useEffect(() => {
    if (['inbox','sent','drafts','trash','scheduled'].includes(activeFolder)) {
      fetchFolder(activeFolder);
    }
  }, [activeFolder, fetchFolder]);

  const refreshFolder = useCallback((folder) => {
    const f = folder || activeFolder;
    loadedFolders.current.delete(f);
    fetchFolder(f, true);
  }, [activeFolder, fetchFolder]);

  const folderEmails = useCallback((folder) => {
    let base = folder === 'starred'
      ? emails.filter(e => e.starred)
      : emails.filter(e => e.folder === folder);
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(e =>
      e.subject?.toLowerCase().includes(q) ||
      e.from?.name?.toLowerCase().includes(q) ||
      e.from?.email?.toLowerCase().includes(q) ||
      e.preview?.toLowerCase().includes(q)
    );
  }, [emails, search]);

  const unreadCount = useCallback((folder) =>
    emails.filter(e => e.folder === folder && e.unread).length, [emails]);

  const openEmail = useCallback(async (email) => {
    setActiveEmail(email);
    if (email.unread && email.uid) {
      try {
        await apiFetch(`/mail/${email.uid}/read`, 'PATCH');
        setEmails(prev => prev.map(e => e.id === email.id ? { ...e, unread: false } : e));
      } catch {}
    }
  }, []);

  const sendEmail = useCallback(async ({ to, cc, bcc, subject, body, scheduledFor, requestReadReceipt }) => {
    const messageId = `msg-${Date.now()}`;
    if (scheduledFor) {
      await apiFetch('/mail/schedule', 'POST', { to, cc, subject, body, scheduledFor: scheduledFor.toISOString() });
      loadedFolders.current.delete('scheduled');
      return { scheduled: true };
    }
    const result = await apiFetch('/mail/send', 'POST', { to, cc, bcc, subject, body, requestReadReceipt, messageId });
    const sentEntry = {
      id: messageId, uid: null, folder: 'sent', unread: false,
      from: { name: 'Me', email: '', initials: 'ME', color: 'blue' },
      to: [{ name: to, email: to }],
      subject, preview: body.replace(/<[^>]+>/g, '').slice(0, 100),
      body, date: new Date(), attachments: [],
      readReceipt: requestReadReceipt, starred: false, labels: [],
    };
    setEmails(prev => [sentEntry, ...prev]);
    setUndoQueue(prev => [...prev, messageId]);
    clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setUndoQueue(prev => prev.filter(id => id !== messageId));
      loadedFolders.current.delete('sent');
    }, 10000);
    return { id: messageId, undoable: true, serverMessageId: result.messageId };
  }, []);

  const recallEmail = useCallback((id) => {
    setEmails(prev => prev.filter(e => e.id !== id));
    setUndoQueue(prev => prev.filter(uid => uid !== id));
    clearTimeout(undoTimerRef.current);
  }, []);

  const deleteEmail = useCallback(async (id) => {
    const email = emails.find(e => e.id === id);
    setEmails(prev => prev.map(e => e.id === id ? { ...e, folder: 'trash' } : e));
    if (activeEmail?.id === id) setActiveEmail(null);
    if (email?.uid) { try { await apiFetch(`/mail/${email.uid}`, 'DELETE'); } catch {} }
  }, [emails, activeEmail]);

  const toggleStar = useCallback((id) => {
    setEmails(prev => prev.map(e => e.id === id ? { ...e, starred: !e.starred } : e));
  }, []);

  const saveDraft = useCallback(({ to, subject, body }) => {
    const draft = {
      id: Date.now().toString(), folder: 'drafts', unread: false,
      from: { name: 'Me', email: '', initials: 'ME', color: 'blue' },
      to: [{ name: to, email: to }],
      subject: subject || '(No subject)',
      preview: body.replace(/<[^>]+>/g, '').slice(0, 100),
      body, date: new Date(), attachments: [],
      readReceipt: false, starred: false, labels: [],
    };
    setEmails(prev => [draft, ...prev]);
  }, []);

  const cancelScheduled = useCallback(async (id) => {
    const email = emails.find(e => e.id === id);
    try { await apiFetch(`/mail/scheduled/${id}`, 'DELETE'); } catch {}
    setEmails(prev => prev.filter(e => e.id !== id));
    if (email) {
      setComposeData({ to: email.to?.[0]?.email, subject: email.subject, body: email.body });
      setComposeOpen(true);
    }
  }, [emails]);

  const openCompose = useCallback((prefill = null) => {
    setComposeData(prefill);
    setComposeOpen(true);
  }, []);

  return (
    <MailContext.Provider value={{
      emails, loadingFolder, fetchError,
      activeFolder, setActiveFolder,
      activeEmail, openEmail, setActiveEmail,
      composeOpen, setComposeOpen, composeData, openCompose,
      folderEmails, unreadCount,
      sendEmail, recallEmail, deleteEmail, toggleStar,
      saveDraft, cancelScheduled,
      undoQueue, search, setSearch, refreshFolder,
    }}>
      {children}
    </MailContext.Provider>
  );
}

export const useMail = () => useContext(MailContext);
